// Typed API client for the admin Repair Project flow.
// Wraps Auth.fetchWithIdToken so callers get strong types and a single
// place to evolve the repair endpoint shapes.

import Auth from "../Auth";

export type DocRef = {
  id: string;
  title: string;
};

export type AuditDocRef = DocRef & {
  location: "contents" | "deletedDocuments" | "none";
  status: "correct" | "external" | "missing";
  parentId: string;
};

export type BackupAvailability = {
  timestamp: number;
  readTime: string;
  available: boolean;
  folderCount: number;
  documentCount: number;
};

export type Diagnosis = {
  projectId: string;
  auditedAt: string;
  missingFromTree: AuditDocRef[];
  externalDocs: AuditDocRef[];
  trashedDocs: AuditDocRef[];
  folders: { count: number; backup: BackupAvailability };
  summary: { correct: number; external: number; missing: number; total: number };
};

export type RepairChange = {
  id: string;
  title: string;
  action: string;
};

export type StepId = "restore-missing" | "remove-external" | "restore-folders";

type BaseResult = {
  success: true;
  dryRun: boolean;
  changed: RepairChange[];
  audit?: Diagnosis;
};

export type RestoreMissingResult = BaseResult & { restoredCount: number };
export type RemoveExternalResult = BaseResult & { removedCount: number };
export type DocMove = { id: string; title: string; from: string; to: string };

// A folder in the previewed new tree with its nested children (docs + subfolders).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PreviewTreeNode = any;

export type RestoreStructureResult = BaseResult & {
  source: string;
  totalFolders: number;
  totalDocuments: number;
  remapped: number;
  unmatched: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  unmatchedDocs: any[];
  reparentedFolders: number;
  reparentedDocs: number;
  unresolvedParentIds: string[];
  tree?: PreviewTreeNode[];
  rootDocs?: PreviewTreeNode[];
  moves?: DocMove[];
};

export type StepResult = RestoreMissingResult | RemoveExternalResult | RestoreStructureResult;

export async function diagnose(projectId: string, timestamp?: number): Promise<Diagnosis> {
  const response = await Auth.fetchWithIdToken(`/api/admin/projects/${projectId}/repair/diagnose`, { timestamp });
  return response as Diagnosis;
}

export async function restoreMissing(projectId: string, dryRun: boolean): Promise<RestoreMissingResult> {
  return await Auth.fetchWithIdToken(`/api/admin/projects/${projectId}/repair/restore-missing`, { dryRun });
}

export async function removeExternal(projectId: string, dryRun: boolean): Promise<RemoveExternalResult> {
  return await Auth.fetchWithIdToken(`/api/admin/projects/${projectId}/repair/remove-external`, { dryRun });
}

export type UploadedBackupPayload = {
  projectId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contents: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  folders: any[];
};

export async function restoreStructure(
  projectId: string,
  options: {
    dryRun: boolean;
    timestamp?: number;
    backupData?: UploadedBackupPayload;
    // On dry-run, include missing docs so the preview tree shows each in its
    // final placement (backup folder if present, else root) rather than all
    // piled at root.
    missingDocs?: { id: string; title: string }[];
  }
): Promise<RestoreStructureResult> {
  return await Auth.fetchWithIdToken(`/api/admin/projects/${projectId}/repair/restore-structure`, {
    dryRun: options.dryRun,
    timestamp: options.timestamp,
    backupData: options.backupData,
    missingDocs: options.missingDocs,
  });
}

/**
 * Fetches raw backup JSON ({projectId, contents, folders}) for a given PITR
 * timestamp. Used to let staff preserve the backup locally before its 7-day
 * retention window expires.
 */
export async function fetchAutomaticBackupRaw(
  projectId: string,
  timestamp: number
): Promise<{ raw: { projectId: string; contents: unknown[]; folders: unknown[] }; readTime: string }> {
  const response = await Auth.fetchWithIdToken(`/api/admin/projects/${projectId}/pitr-structure`, { timestamp });
  return response as { raw: { projectId: string; contents: unknown[]; folders: unknown[] }; readTime: string };
}
